import React, { ReactChild, useContext, useEffect, useState } from 'react'
import withSimpleApi, { WithSimpleApiProps } from '~/decorators/withSimpleApi'
import { CallListManagerContext, CallOnList } from '~/service/CallListManager'
import HistoryBack from '../common/HistoryBack'
import { ListOnly } from '../calls/List'
import { SipContext } from '~/service/SipConn'
import { CampaignManagerContext } from '~/service/CampaignManager'
import { FormattedMessage } from '~/translations/utils'
import { formatPhoneNumber } from '~/utils/string'
import { useMounted } from '~/hooks/mounted'
import MainContent from '../MainContent'
import ClientHeader from '../common/ClientHeader'

type ContactPerson = {
  id: number
  name: string
  phone: string
  email: string
}

enum ContactType {
  EMAIL = 1,
  MOBILE = 2,
  PHONE = 3,
  WEBSITE = 4,
}

enum CustomFieldType {
  TEXT = 1,
  CHECKBOX = 2,
  SELECT = 3,
}

type CustomField = {
  name: string
  type: CustomFieldType
}

type Contact = {
  id: number
  type: ContactType | null
  value: string
  customField: CustomField | null
}

type ContactNormal = Contact & {
  type: ContactType
}

type ContactCustomField = Contact & {
  customField: CustomField
}

export type ClientOnShow = {
  id: number
  name: string
  avatar: string
  externalId: string
  contactPersons: ContactPerson[]
  contacts: Contact[]
  nippesel: string
  address: string
  correspondenceAddress: string
  tmBan: number | null
  tmCampaigns: number[]
}

type ApiDataType = {
  client: ClientOnShow
  calls: CallOnList[]
}

const Show: React.FC<WithSimpleApiProps<ApiDataType>> = ({ apiData }) => {
  const callListContext = useContext(CallListManagerContext)

  const { client } = apiData

  const [calls, setCalls] = useState<CallOnList[]>(apiData.calls)

  const mountedRef = useMounted()

  useEffect(() => {
    const { channel } = callListContext

    if (!channel) {
      return
    }

    channel.on('created', (data: CallOnList) => {
      if (data.client.id != client.id || !mountedRef.current) {
        return
      }

      setCalls(items => [data, ...items])
    })

    channel.on('updated', (data: CallOnList) => {
      if (data.client.id != client.id || !mountedRef.current) {
        return
      }

      setCalls(items =>
        items.map(item =>
          item.id == data.id ? { ...data, ver: (item.ver || 0) + 1 } : item
        )
      )
    })
  }, [])

  return (
    <MainContent className="is-whiteBg client-show">
      <HistoryBack />

      <ClientHeader client={client} nameAsLink={false} />

      <div className="client-show-scroll">
        <ClientData client={client} />

        <h2>
          <span className="far fa-history" />
          <FormattedMessage id="client / history" />
        </h2>
        <ListOnly items={calls} />
      </div>
    </MainContent>
  )
}
const ShowWithApi = withSimpleApi(Show)

export default ShowWithApi

type ClientDataProps = {
  client: ClientOnShow
}

const ClientData: React.FC<ClientDataProps> = ({ client }) => {
  const { activeCampaign, campaigns } = useContext(CampaignManagerContext)

  const ContactTypeLabel = {
    [ContactType.EMAIL]: intl.formatMessage({
      id: 'client / contact type / email',
    }),
    [ContactType.MOBILE]: intl.formatMessage({
      id: 'client / contact type / mobile',
    }),
    [ContactType.PHONE]: intl.formatMessage({
      id: 'client / contact type / phone',
    }),
    [ContactType.WEBSITE]: intl.formatMessage({
      id: 'client / contact type / website',
    }),
  }

  const ContactTypeIcon = {
    [ContactType.EMAIL]: 'far fa-at',
    [ContactType.MOBILE]: 'fa fa-phone',
    // [ContactType.PHONE]: 'fa fa-phone-office',
    [ContactType.PHONE]: 'fa fa-phone',
    [ContactType.WEBSITE]: 'far fa-globe',
  }

  const tilesPhone: ClientDataTilePropsWithoutClient[] = []
  const tilesRest: ClientDataTilePropsWithoutClient[] = []

  client.nippesel &&
    tilesRest.push({
      icon: 'far fa-id-card',
      label: 'ID',
      value: client.nippesel,
    })

  client.address &&
    tilesRest.push({
      icon: 'far fa-home',
      label: intl.formatMessage({ id: 'client / data / address' }),
      value: client.address.replace(/\n/g, ', '),
    })

  client.correspondenceAddress &&
    tilesRest.push({
      icon: 'far fa-mailbox',
      label: intl.formatMessage({
        id: 'client / data / correspondence address',
      }),
      value: client.correspondenceAddress.replace(/\n/g, ', '),
    })
  //
  ;(
    client.contacts.filter(contact => !contact.customField) as ContactNormal[]
  ).forEach(contact => {
    const props = {
      icon: ContactTypeIcon[contact.type],
      label: ContactTypeLabel[contact.type],
      value:
        contact.type == ContactType.WEBSITE ? (
          <a href={contact.value} target="_blank">
            {contact.value}
          </a>
        ) : (
          contact.value
        ),
    }

    if ([ContactType.PHONE, ContactType.MOBILE].includes(contact.type)) {
      tilesPhone.push({ ...props, isPhone: true })
    } else {
      tilesRest.push(props)
    }
  })

  client.contactPersons.forEach(person => {
    person.phone &&
      tilesPhone.push({
        icon: ContactTypeIcon[ContactType.MOBILE],
        label: ContactTypeLabel[ContactType.MOBILE] + ' - ' + person.name,
        value: person.phone,
        isPhone: true,
      })

    person.email &&
      tilesRest.push({
        icon: ContactTypeIcon[ContactType.EMAIL],
        label: ContactTypeLabel[ContactType.EMAIL] + ' - ' + person.name,
        value: person.email,
      })
  })
  //
  ;(
    client.contacts.filter(
      contact => contact.customField
    ) as ContactCustomField[]
  )
    .sort((a, b) => a.customField.name.localeCompare(b.customField.name))
    .forEach(contact => {
      const props =
        contact.customField.type != CustomFieldType.CHECKBOX
          ? { value: contact.value }
          : { valueCheckbox: !!contact.value }

      tilesRest.push({
        icon: 'far fa-pen',
        label: contact.customField.name,
        ...props,
      })
    })

  return (
    <>
      {tilesPhone.length > 0 && (
        <>
          <h2>
            <span className="far fa-comments-alt" />
            <FormattedMessage id="client / call" />
          </h2>
          <div className="client-show-data">
            {tilesPhone.map((props, k) => (
              <ClientDataTilePhone key={k} client={client} {...props} />
            ))}
          </div>

          {campaigns
            .filter(campaign => client.tmCampaigns.includes(campaign.id))
            .map(campaign => (
              <React.Fragment key={campaign.id}>
                <h2>
                  <span className="far fa-bullhorn" />
                  <FormattedMessage id="client / call campaign" />{' '}
                  {campaign.name}
                </h2>
                <div className="client-show-data">
                  {tilesPhone.map((props, k) => (
                    <ClientDataTilePhone
                      tmCampaignId={campaign.id}
                      key={k}
                      client={client}
                      {...props}
                    />
                  ))}
                </div>
              </React.Fragment>
            ))}
        </>
      )}

      {tilesRest.length > 0 && (
        <>
          <h2>
            <span className="far fa-user-edit" />

            <FormattedMessage id="client / data" />
          </h2>
          <div className="client-show-data">
            {tilesRest.map((props, k) => (
              <ClientDataTile key={k} client={client} {...props} />
            ))}
          </div>
        </>
      )}
    </>
  )
}

type ClientDataTilePropsWithoutClient = {
  icon?: string
  label: string
  value?: ReactChild
  valueCheckbox?: boolean
  isPhone?: boolean
}

type TileProps = {
  icon?: string
  label: string
  children: React.ReactNode
}

const Tile: React.FC<TileProps> = ({ icon, label, children }) => {
  return (
    <>
      <figure>{icon && <span className={icon} />}</figure>
      <main>
        <div className="client-show-data-tile-label">{label}</div>
        <div className="client-show-data-tile-value">{children}</div>
      </main>
    </>
  )
}

type ClientDataTileProps = ClientDataTilePropsWithoutClient & {
  client: ClientOnShow
}

const ClientDataTile: React.FC<ClientDataTileProps> = ({
  icon,
  label,
  value,
  valueCheckbox,
}) => (
  <div className="client-show-data-tile">
    <Tile icon={icon} label={label}>
      {typeof value !== 'undefined' ? (
        value
      ) : (
        <span
          className={valueCheckbox ? 'fa fa-check-square' : 'far fa-square'}
        />
      )}
    </Tile>
  </div>
)

type ClientDataTilePhoneProps = ClientDataTilePropsWithoutClient & {
  client: ClientOnShow
  tmCampaignId?: number
}

const ClientDataTilePhone: React.FC<ClientDataTilePhoneProps> = ({
  icon,
  label,
  value,
  client,
  tmCampaignId,
}) => {
  const { makeCall } = useContext(SipContext)
  const { activeCampaign } = useContext(CampaignManagerContext)

  const inner = (
    <Tile icon={icon} label={label}>
      {formatPhoneNumber(value as string)}
    </Tile>
  )

  const clientNotInActiveCampaign =
    activeCampaign && !client.tmCampaigns.includes(activeCampaign.id)

  const campaignIsNotActive =
    activeCampaign && (!tmCampaignId || tmCampaignId != activeCampaign.id)

  return !clientNotInActiveCampaign && !campaignIsNotActive ? (
    <button
      type="button"
      className="client-show-data-tile is-phone"
      onClick={() =>
        tmCampaignId
          ? makeCall(value as string, {
              tmCampaign: tmCampaignId,
              clientId: client.id,
            })
          : makeCall(value as string, {
              clientId: client.id,
            })
      }
    >
      {inner}
    </button>
  ) : (
    <div className="client-show-data-tile">{inner}</div>
  )
}
